/** @format */
import { Checkbox, FormControlLabel, FormGroup } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import CustomSmallTextButton from '../../components/Buttons/CustomSmallTextButton';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { configDefault } from '../../core/config';
import { useDispatch, useSelector } from 'react-redux';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { prefixesConstants } from '../../modules/prefixes/reducers';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import InputFormRest from '../../components/Form/InputFormRest';
import SimpleSelectRest from '../../components/Adescom/SimpleSelectRest';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { fractionsConstants } from '../../modules/fractions/actions';
import { fractionsReducerType } from '../../modules/fractions/reducers';
import PrefixInterface from '../../interfaces/billing/PrefixInterface';
import I18n from '../../components/Translate/I18n';
import CardHeader from '../../components/Heading/CardHeaderExt';
import Card from '../../components/Card/Card';
import CardBody from '../../components/Card/CardBody';
import { withStyles } from '@material-ui/core/styles';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';

interface PrefixAddProps {
    classes: any;
    prefix?: PrefixInterface;
    editMode?: boolean;
}

const PrefixAdd = (props: PrefixAddProps): React.ReactElement => {
    const dispatch = useDispatch();

    const URI = '/prefixes';
    const FRACTION_URI = '/fractions';

    const [entityStatus, setEntityStatus] = useState<PrefixInterface>({
        mainname: '',
        prefix: '',
        type: null,
        fractionId: null,
        usageFlag: -1,
    });

    const { collections } = useSelector((state: { fractionsReducers: fractionsReducerType }) => {
        return state.fractionsReducers;
    });

    const [entity, setEntity] = useState<PrefixInterface>(
        props.prefix
            ? {
                  id: props.prefix.id,
                  mainname: props.prefix.mainname,
                  prefix: props.prefix.prefix,
                  type: props.prefix.type,
                  fractionId: props.prefix.fractionId,
                  usageFlag: props.prefix.usageFlag,
              }
            : {
                  mainname: '',
                  prefix: '',
                  type: null,
                  fractionId: null,
                  usageFlag: -1,
              }
    );

    const savePrefix = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        if (props.editMode) {
            itemLoaders.putWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${URI}/${entity.id}`, configDefault.PLATFORM_API_PREFIX)),
                prefixesConstants.EDIT,
                entity,
                {
                    onSuccess: (prefix: PrefixInterface) => {
                        NotificationHelper.notifySaveSuccess(dispatch);
                    },
                    onError: (error: any) => {
                        if (error && error.message && error.message.message) {
                            NotificationHelper.notifyError(error.message.message, dispatch);
                        } else {
                            NotificationHelper.notifyDefaultError(dispatch);
                        }
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchErrorAfterCallback: false,
                }
            );
        } else {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(URI, configDefault.PLATFORM_API_PREFIX)),
                prefixesConstants.ADD,
                entity,
                {
                    onSuccess: (prefix: PrefixInterface) => {
                        NotificationHelper.notifySaveSuccess(dispatch);
                    },
                    onError: (error: any) => {
                        console.log(error);
                        if (error && error.message && error.message.message) {
                            NotificationHelper.notifyError(error.message.message, dispatch);
                        } else {
                            NotificationHelper.notifyDefaultError(dispatch);
                        }
                    },
                },
                {
                    propagateDataToCallback: true,
                }
            );
        }
    };

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        let value = event.target.value;
        const checked = event.target.checked;
        if (name === 'typeUsage') {
            if (!checked) {
                value = '-1';
            }
            setEntity({ ...entity, usageFlag: parseInt(value) });
        }
    };

    const changeInput = (e: any, stateName: string, type: string, require: boolean, maxValue: number, minValue: number): void => {
        // TODO: Add validation (use entityStatus)
        setEntity({ ...entity, [stateName]: e.target.value });
    };

    const changeSelect = (e: any, stateName: string, require: boolean): void => {
        // TODO: Add validation (use entityStatus)
        setEntity({ ...entity, [stateName]: e.target.value });
    };

    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(FRACTION_URI, configDefault.PLATFORM_API_PREFIX)),
            fractionsConstants.LIST,
            [{ name: 'itemsPerPage', value: 9999 }],
            [{ name: 'mainname', value: 'asc' }],
            (data: any) => {},
            {
                propagateDataToCallback: false,
                dispatchAfterCallback: true,
            }
        );
    }, [dispatch]);

    return (
        <Card>
            {!props.editMode ? <CardHeader trans="prefixes:h.prefix_add" /> : null}
            <CardBody>
                <GridContainer>
                    <GridItem xs={6}>
                        <GridContainer spacing={2}>
                            <GridItem xs={12}>
                                <InputFormRest
                                    classes={props.classes}
                                    callback={changeInput}
                                    stateName="mainname"
                                    stateObjectState={entityStatus.mainname}
                                    typeCallback={FormGeneralHelper.TYPE_TEXT}
                                    typeField={FormGeneralHelper.TYPE_TEXT}
                                    displayFieldName="prefixes:h.prefix_name"
                                    required={false}
                                    vMax={255}
                                    vMin={0}
                                    value={entity.mainname}
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <InputFormRest
                                    classes={props.classes}
                                    callback={changeInput}
                                    stateName="prefix"
                                    stateObjectState={entityStatus.prefix}
                                    typeCallback={FormGeneralHelper.TYPE_TEXT}
                                    typeField={FormGeneralHelper.TYPE_TEXT}
                                    displayFieldName="prefixes:h.prefix"
                                    required={false}
                                    vMax={255}
                                    vMin={0}
                                    value={entity.prefix}
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <SimpleSelectRest
                                    callback={changeSelect}
                                    restElements={{
                                        'hydra:member': [
                                            { name: 'Połączenie głosowe', value: 0 },
                                            { name: 'Usługa', value: 1 },
                                        ],
                                    }}
                                    stateName="type"
                                    idState="type"
                                    idSelected={entity.type}
                                    propKey="value"
                                    required={false}
                                    propValue="name"
                                    emptyLabel="general:common.empty_label"
                                    label="prefixes:h.type"
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <SimpleSelectRest
                                    callback={changeSelect}
                                    restElements={collections}
                                    stateName="fractionId"
                                    idState="fractionId"
                                    idSelected={entity.fractionId}
                                    propKey="id"
                                    required={false}
                                    propValue="mainname"
                                    emptyLabel="general:common.empty_label"
                                    label="prefixes:h.fraction"
                                />
                            </GridItem>
                            <GridItem>
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Checkbox checked={entity.usageFlag !== -1} onChange={onCheckBoxEvent} value={1} name="typeUsage" />}
                                        label={<I18n trans="prefixes:h.generate_cdr" />}
                                    />
                                </FormGroup>
                            </GridItem>
                            <GridItem xs={12}>
                                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PREFIX_POST) && (
                                    <CustomSmallTextButton label={'prefixes:b.save_prefix'} callback={savePrefix} callbackParam={{}} />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </CardBody>
        </Card>
    );
};

export default withStyles(oplAppStyle)(PrefixAdd);
